import React from 'react';
import { connect } from 'react-redux';
import { GridToolbar } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { GridSize, Grid } from '../../shared/components';
import {
  loadGeneralCompetencyEventListPage,
  findGeneralCompetencyEvents,
  sortGeneralCompetencyEvents,
  openGeneralCompetencyEventConfirmDialog,
  closeGeneralCompetencyEventConfirmDialog,
  removeGeneralCompetencyEventConfirmDialog
} from '../actions/general-competency-event-list-page-actions';
import { withRouter } from '../../../components/withRouter';
import { GENERAL_COMPETENCY_EVENT_STATUSES } from '../../../resources/generalCompetencyEventStatus';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Text } from '../../../ui';
import { ErrorMessage } from '../../../ui/components/common';

class GeneralCompetencyEventList extends React.PureComponent {

  constructor() {
    super();
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.handleAddClicked = this.handleAddClicked.bind(this);
    this.handleRemoveClick = this.handleRemoveClick.bind(this);
    this.handleConfirmDialogClose = this.handleConfirmDialogClose.bind(this);
    this.handleRemoveConfirm = this.handleRemoveConfirm.bind(this);
  }

  async componentDidMount() {
    await this.props.loadGeneralCompetencyEventListPage();
  }

  render() {
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <Grid
          loading={this.props.loading}
          data={this.props.generalCompetencyEvents}
          skip={this.props.skip}
          take={this.props.take}
          total={this.props.total}
          sort={this.props.sorting}
          onPageChange={this.handlePageChange}
          onSortChange={this.handleSort}
          onRowClick={this.handleRowClick}
          rowLink={(dataItem) => `/general_competency_events/${dataItem.id}/overview`}
          columns={[
            {
              field: 'name',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('generalCompetencyEvent.name')
            },
            {
              field: 'startDate',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('generalCompetencyEvent.startDate'),
              minGridWidth: GridSize.small,
              format: '{0:yyyy-MM-dd}'
            },
            {
              field: 'endDate',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('generalCompetencyEvent.endDate'),
              minGridWidth: GridSize.small,
              format: '{0:yyyy-MM-dd}'
            },
            {
              field: 'sessionCount',
              className: 'align-right',
              headerClassName: 'align-right',
              title: localizationService.toLanguageString('generalCompetencyEvent.sessionCount'),
              minGridWidth: GridSize.small,
              sortable: false
            },
            {
              field: 'status',
              className: 'align-left',
              headerClassName: 'align-left',
              title: localizationService.toLanguageString('generalCompetencyEvent.status'),
              minGridWidth: GridSize.small,
              sortable: false,
              cell: (e) => this.renderStatus(e, localizationService)
            }
          ]}
          actions={
            this.props.canWrite && [{
              icon: 'delete',
              onClick: this.handleRemoveClick,
              disabledOn: (e) => e.dataItem.status !== GENERAL_COMPETENCY_EVENT_STATUSES.DRAFT
            }]
          }
        >
          <GridToolbar>
            {this.props.canWrite &&
              <Button themeColor='primary' onClick={this.handleAddClicked}>
                {localizationService.toLanguageString('custom.add')}
              </Button>
            }
          </GridToolbar>
        </Grid>
        {this.props.isConfirmDialogVisible &&
          <Dialog title={localizationService.toLanguageString('custom.confirm')} onClose={this.handleConfirmDialogClose}>
            {this.props.confirmDialogErrorMessage &&
              <ErrorMessage>{this.props.confirmDialogErrorMessage}</ErrorMessage>
            }
            <Text>{localizationService.toLanguageString('generalCompetencyEvent.deleteConfirmDialogMessage')}</Text>
            <DialogActionsBar>
              <Button themeColor={'primary'} onClick={this.handleRemoveConfirm}>{localizationService.toLanguageString('buttons.delete')}</Button>
              <Button onClick={this.handleConfirmDialogClose}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  renderStatus(e, localizationService) {
    const { className, dataItem } = e;
    const statusMap = {
      'Draft': 'generalCompetencyEvent.statusDraft',
      'Published': 'generalCompetencyEvent.statusPublished',
      'Completed': 'generalCompetencyEvent.statusCompleted',
      'Cancelled': 'generalCompetencyEvent.statusCancelled'
    };
    const key = statusMap[dataItem.status];
    return (
      <td className={className}>
        {key ? localizationService.toLanguageString(key) : dataItem.status}
      </td>
    );
  }

  handleSort(e) {
    this.props.sortGeneralCompetencyEvents(e.sort);
  }

  async handlePageChange(e) {
    await this.props.findGeneralCompetencyEvents({ skip: e.page.skip, take: e.page.take });
  }

  handleRowClick(e) {
    this.props.navigate(`/general_competency_events/${e.dataItem.id}/overview`);
  }

  handleAddClicked() {
    this.props.navigate('/general_competency_events/create');
  }

  handleRemoveClick(row) {
    this.props.openGeneralCompetencyEventConfirmDialog(row.dataItem);
  }

  handleConfirmDialogClose() {
    this.props.closeGeneralCompetencyEventConfirmDialog();
  }

  handleRemoveConfirm() {
    this.props.removeGeneralCompetencyEventConfirmDialog();
  }

}

const mapStateToProps = state => ({
  loading: state.generalCompetencyEventListPage.loading,
  generalCompetencyEvents: state.generalCompetencyEventListPage.generalCompetencyEvents,
  skip: state.generalCompetencyEventListPage.skip,
  take: state.generalCompetencyEventListPage.take,
  total: state.generalCompetencyEventListPage.total,
  sorting: state.generalCompetencyEventListPage.sorting,
  canWrite: state.generalCompetencyEventListPage.canWrite,
  isConfirmDialogVisible: state.generalCompetencyEventListPage.isConfirmDialogVisible,
  confirmDialogErrorMessage: state.generalCompetencyEventListPage.confirmDialogErrorMessage
});

const mapDispatchToProps = dispatch => ({
  loadGeneralCompetencyEventListPage: () => dispatch(loadGeneralCompetencyEventListPage()),
  findGeneralCompetencyEvents: (payload) => dispatch(findGeneralCompetencyEvents(payload)),
  sortGeneralCompetencyEvents: (payload) => dispatch(sortGeneralCompetencyEvents(payload)),
  openGeneralCompetencyEventConfirmDialog: (payload) => dispatch(openGeneralCompetencyEventConfirmDialog(payload)),
  closeGeneralCompetencyEventConfirmDialog: () => dispatch(closeGeneralCompetencyEventConfirmDialog()),
  removeGeneralCompetencyEventConfirmDialog: (payload) => dispatch(removeGeneralCompetencyEventConfirmDialog(payload))
});

registerForLocalization(GeneralCompetencyEventList);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(GeneralCompetencyEventList));
